import React, { FC, useEffect, useRef, useCallback, KeyboardEventHandler, RefObject } from 'react';

import { CloseIcon } from '../SvgIcons';
import { useScrollLock } from '../../hooks/useAddFeatureFlagToRoot/useScrollLock';
import useFocusTrap from '../../hooks/useFocusTrap';
import useClickOutside from '../../hooks/useClickOutside';
import ModalContainer from './ModalContainer';
import { Overlay, ModalDialog, CloseModalButton, ScreenReaderText } from './Modal.styles';

export type ModalProps = {
  /** A function to perform when the user closes the modal.  Should remove the modal from the DOM. */
  onClose: () => void;
  /** Where the modal will be mounted.  Should be at the root of the app so that the overlay covers the entire window.  Must be separately defined. */
  rootId: string;
  /** The id for the h1 (preferred) or h2 element that will be the title for the modal. If you cannot add a heading and make it visible, you may make the heading visible only to screen reader users. Accessibility Requirement */
  dialogHeadingId: string;
  /** The modal should open based on some user action, preferably from a button element.  Focus will return to the element upon closing the modal.   Accessibility requirement. */
  triggerRef: RefObject<HTMLButtonElement | HTMLAnchorElement> | null;
  className?: string;

  /** Locks background scrolling in place.  Defaults to false in Modal and ModalV2.  We would like this to default to true, but the scroll lock jumps the background to the top top of the page, so there is a tradeoff to be mad. */
  lockBgScrolling?: boolean;

  /** Show the close button. Default true: show the close button */
  showCloseButton?: boolean;

  /** Should clicking outside the modal close it.  Default true: close the modal on click outside. */
  closeOnOverlayClick?: boolean;

  /** Should the modal close when the 'escape' key is press.  Default true: close the modal on escape. */
  closeOnEscape?: boolean;
};

/**
 * Modal is deprecated.
 *
 * This version of the modal:
 *
 * - Has padding around the content container.  This forces clients to override the padding to meet the design system or push content (images) all the way to the edge of the modal
 * - Lacks mobile styles.  It uses 100px margins for all device widths, so this forces clients to override the styles to use this modal on mobile.  Without the overrides, the maximum content width for the modal is 100vw - 200px.
 * - Uses a flex layout for the close button and content.  The close button pushes the content of the modal down by about 20px.  This means that you can't have modals with background colors or images that align to the top of the modal.
 *
 * To migrate to ModalV2:
 *
 * - Switch to ModalV2
 * - Remove overrides for mobile, padding, close buttons
 * - Verify the modal content / presentation
 *
 * @deprecated Modal is deprecated in favor of ModalV2.
 *
 */
const Modal: FC<ModalProps> = ({
  children,
  dialogHeadingId = 'dialog-heading',
  onClose,
  rootId,
  triggerRef,
  className,
  lockBgScrolling = false,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const handleTabbing = useFocusTrap(dialogRef);
  useScrollLock(lockBgScrolling);

  const onCloseCallback = useCallback(onClose, [onClose]);

  useClickOutside({
    ref: dialogRef,
    onClickOutside: onCloseCallback,
    isOpen: true && closeOnOverlayClick,
  });

  useEffect(() => {
    // Close modal when hitting "Esc".
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        event.stopPropagation();
        onCloseCallback();
      }
    };
    window.addEventListener('keydown', close, true);
    const triggeringButton = triggerRef?.current;

    return function removeEventListenerAndReturnFocus() {
      window.removeEventListener('keydown', close, true);
      if (triggeringButton) {
        triggeringButton.focus();
      }
    };
  }, [closeOnEscape, onCloseCallback, triggerRef]);

  const handlePressEnter: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onCloseCallback();
    }
  };

  return (
    <ModalContainer rootId={rootId}>
      <Overlay className={className}>
        <ModalDialog
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogHeadingId}
          ref={dialogRef}
          onKeyDown={handleTabbing}
        >
          {showCloseButton && (
            <CloseModalButton
              id="zCloseModal"
              onClick={onCloseCallback}
              onKeyDown={handlePressEnter}
              ref={closeButtonRef}
            >
              <CloseIcon showTitle={false} />
              <ScreenReaderText>Close</ScreenReaderText>
            </CloseModalButton>
          )}
          {children}
        </ModalDialog>
      </Overlay>
    </ModalContainer>
  );
};

export default Modal;
